import useOSS from "@/hooks/useRequest/useOSS";
import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { canUseDom } from "@/utils/components-utils";
import compressorFile from "@/utils/compressorFile";
import supportWebP from "@/utils/supportWebp";
import log2Sentry from "@/utils/log2Sentry";
import getRandomFileName from "@/utils/getRadomFileName";
import Loading from "../../containers/activities/LoadingPoster";
import { createRoot } from "react-dom/client";

export interface PosterResult {
	imageId: string;
	url: string;
}

interface QrCode {
	w: number;
	x: number;
	y: number;
	txt: string; // 二维码链接地址
}

interface DrawText {
	txt: string;
	color: string;
	fontStyle: string;
	letterSpacing?: number;
	textDecoration?: string;
	strokeStyle?: string;
	lineHeight: number;
	maxWidth: number;
	x: number;
	y: number;
}

interface DrawImg {
	x: number;
	y: number;
	w: number;
	h: number;
	url: string;
}

interface PosterOptions {
	w: number;
	h: number;
	bg?: string;
	bgColor?: string;
	qrCode?: QrCode;
	texts?: DrawText[];
	images?: DrawImg[];
}
/**
 * canvas 生成海报
 */
export default function usePoster() {
	const { upload } = useOSS("poster");
	const divRef = useRef<any>(null);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!canUseDom) return;
		if (loading) {
			const div = document.createElement("div");
			divRef.current = div;
			document.body.appendChild(div);
			const root = createRoot(div);
			root.render(<Loading />);
		} else {
			if (divRef && divRef.current) {
				document.body.removeChild(divRef.current);
				divRef.current = null;
			}
		}
	}, [loading]);

	const onShare = async (options: PosterOptions) => {
		try {
			if (!canUseDom) throw Error("missing window");

			const canvas = document.createElement("canvas");
			if (!canvas) throw Error("missing canvas");
			setLoading(true);

			const { w, h, bg, bgColor, qrCode, texts, images } = options;
			const rato = window.devicePixelRatio || 1;
			canvas.width = w * rato;
			canvas.height = h * rato;
			canvas.style.width = w + "px";
			canvas.style.height = h + "px";

			const ctx = canvas.getContext("2d") as CanvasRenderingContext2D;
			ctx.scale(rato, rato);

			if (bgColor) {
				await drawBgColor(ctx, bgColor, w + 20, h + 20);
			}
			if (bg) {
				await handleDrawBackground(w, h, bg, ctx);
			}

			if (qrCode) {
				await handleDrawQrCode(ctx, qrCode);
			}
			if (images && images.length > 0) {
				await drawContentImages(images, ctx);
			}
			if (texts && texts.length > 0) {
				texts.forEach((item) => {
					drawText(item, ctx);
				});
			}
			const res = await uploadImg(canvas);

			return res;
		} catch (error) {
			console.log(error);
			log2Sentry(Error("生成海报报错"), "error", error);
		} finally {
			setLoading(false);
		}
	};

	const uploadImg = (_canvas: HTMLCanvasElement): Promise<{ url: string; imageId: string }> => {
		return new Promise((resolve, reject) => {
			_canvas.toBlob(
				async (blob) => {
					try {
						if (!blob) throw Error("canvas to blob error");

						const _blob = await compressorFile(blob, {
							maxWidth: 1024,
							quality: 0.9,
						});

						const imageId = getRandomFileName();
						const fileName = imageId + ".webp";

						const res = await upload(_blob, fileName, true);
						console.log(res);
						resolve({
							url: res,
							imageId,
						});
					} catch (error) {
						reject(error);
					}
				},
				supportWebP.getSupportWebP() ? "image/webp" : "image/png",
			);
		});
	};

	const drawContentImages = (images: DrawImg[], ctx: CanvasRenderingContext2D) => {
		if (images.length > 0) {
			const imgPromises = images.map((item) => {
				return drawContentImg(item, ctx);
			});
			return Promise.all(imgPromises);
		}
	};

	const drawBgColor = (ctx: CanvasRenderingContext2D, bgColor: string, w: number, h: number) => {
		return new Promise((resolve, reject) => {
			ctx.fillStyle = bgColor;
			ctx.fillRect(0, -20, w, h);
			resolve(true);
		});
	};

	const drawContentImg = (drawImg: DrawImg, ctx: CanvasRenderingContext2D) => {
		return new Promise((resolve: any, reject: any) => {
			const imgMain = new Image();
			imgMain.crossOrigin = "Anonymous";
			imgMain.onload = () => {
				ctx.drawImage(imgMain, drawImg.x, drawImg.y, drawImg.w, drawImg.h);
				resolve();
			};
			imgMain.onerror = () => {
				reject(new Error("avatar loaded error"));
			};
			imgMain.src = drawImg.url;
		});
	};

	const handleDrawQrCode = async (ctx: CanvasRenderingContext2D, config: QrCode) => {
		try {
			const { txt, w, x, y } = config;
			const str = await QRCode.toCanvas(txt, {
				width: w * 8,
				margin: 1,
				errorCorrectionLevel: "Q",
			});
			ctx.drawImage(str, x, y, w, w);
			return true;
		} catch (error) {
			throw error;
		}
	};

	const handleDrawBackground = async (w: number, h: number, bg: string, ctx: CanvasRenderingContext2D) => {
		return new Promise((resolve: any, reject: any) => {
			const bgImg = new Image();
			bgImg.crossOrigin = "Anonymous";

			bgImg.onload = () => {
				ctx.drawImage(bgImg, 0, 0, w, h);
				resolve("success");
			};

			bgImg.onerror = () => {
				reject(new Error("background image loaded error"));
			};
			bgImg.src = bg;
		});
	};

	const drawText = (config: DrawText, ctx: CanvasRenderingContext2D) => {
		const { txt, maxWidth, lineHeight, x, y, color, fontStyle, letterSpacing, textDecoration, strokeStyle } = config;
		ctx.fillStyle = color;
		ctx.font = fontStyle;
		if (textDecoration) {
			(ctx as any).textDecoration = textDecoration;
		}
		if (strokeStyle) {
			(ctx as any).strokeStyle = strokeStyle;
		}

		const waitDrawTexts = drawTextWithLineBreaks(x, y, txt, letterSpacing || 0, lineHeight, maxWidth, ctx);
		waitDrawTexts.forEach((item) => {
			ctx.fillText(item.txt, item.x, item.y);
		});
	};

	const drawTextWithLineBreaks = (
		x: number,
		y: number,
		text: string,
		letterSpacing: number,
		lineHeight: number,
		maxWidth: number,
		ctx: CanvasRenderingContext2D,
	) => {
		// 换行符号
		const breakSymbols = [".", ".", "!", "，", "。", "！", " "];
		let waitDrawTexts = [];
		let lineX = x;
		let lineY = y;
		let lineCharacters = "";
		let characterIndex = 0;
		let breakIndex = 0;

		while (characterIndex < text.length) {
			const character = text[characterIndex];
			const charWith = ctx.measureText(character).width;
			// 记录空格以及标点符号以遍重新计算
			if (breakSymbols.includes(character)) {
				breakIndex = characterIndex;
			}
			if (lineCharacters !== "") {
				lineX += letterSpacing;
			}
			if (lineX + charWith > maxWidth + x) {
				if (breakIndex > 0) {
					lineX = x;
					lineY += lineHeight;
					lineCharacters = "";
					characterIndex = breakIndex + 1;
					continue;
				} else {
					lineX = x;
					lineY += lineHeight;
					lineCharacters = "";
				}
			}

			waitDrawTexts[characterIndex] = {
				x: lineX,
				y: lineY,
				txt: character,
			};
			lineCharacters += character;
			lineX += charWith;
			characterIndex++;
		}
		return waitDrawTexts;
	};

	return {
		loading,
		onShare,
	};
}
